/*
 * Registry of the marks under /public/icons: connector and product marks in
 * icons/sources (named by provider id), MLX's own marks in icons/mlx, and the
 * AI-client marks in icons/clients. Every place the app shows one resolves it
 * here, so a new integration is added in one spot and a dead or unregistered
 * asset fails the registry test.
 *
 * Keys are the ids the app already uses (connector provider ids, plugin
 * slugs, interface types); aliases fold alternate spellings onto one asset.
 * The admin-control plugin catalogue stores these URLs in tenant databases,
 * so a rename here needs a catalogue migration alongside it.
 */

export const SOURCE_ICON_ROOT = "/icons/sources";
export const CLIENT_ICON_ROOT = "/icons/clients";
export const MLX_ICON_ROOT = "/icons/mlx";

export type BrandIcon = {
  /** Public URL of the mark. */
  src: string;
  label: string;
  /** One-colour marks that need inverting on dark backgrounds. */
  monochrome?: boolean;
};

const SOURCE_ICONS: Record<
  string,
  Omit<BrandIcon, "src"> & { file: string; root?: string }
> = {
  apollo: { file: "apollo.svg", label: "Apollo" },
  copilot: {
    file: "copilot.svg",
    label: "Microsoft Copilot",
    monochrome: true,
  },
  github: { file: "github.png", label: "GitHub" },
  gmail: { file: "gmail.png", label: "Gmail" },
  gocardless: { file: "gocardless.svg", label: "GoCardless" },
  google_calendar: { file: "google-calendar.png", label: "Google Calendar" },
  google_docs: { file: "google-docs.png", label: "Google Docs" },
  google_drive: { file: "google-drive.png", label: "Google Drive" },
  google_slides: { file: "google-slides.png", label: "Google Slides" },
  google_workspace: { file: "google-workspace.svg", label: "Google Workspace" },
  word: { file: "word.svg", label: "Microsoft Word" },
  excel: { file: "excel.svg", label: "Microsoft Excel" },
  powerpoint: { file: "powerpoint.svg", label: "Microsoft PowerPoint" },
  onedrive: { file: "onedrive.svg", label: "Microsoft OneDrive" },
  google_sheets: { file: "google-sheets.png", label: "Google Sheets" },
  granola: { file: "granola.png", label: "Granola" },
  hubspot: { file: "hubspot.svg", label: "HubSpot" },
  linear: { file: "linear.png", label: "Linear", monochrome: true },
  mcp: { file: "mcp.svg", label: "Model Context Protocol", monochrome: true },
  microsoft_365: { file: "microsoft-365.svg", label: "Microsoft 365" },
  mlx_artifacts: {
    file: "artifacts.svg",
    label: "Twin artifacts",
    root: MLX_ICON_ROOT,
  },
  mlx_warehouse: {
    file: "warehouse.svg",
    label: "Twin warehouse",
    root: MLX_ICON_ROOT,
  },
  mlx_workspace: {
    file: "workspace.svg",
    label: "Twin workspace",
    root: MLX_ICON_ROOT,
  },
  notion: { file: "notion.svg", label: "Notion", monochrome: true },
  outlook_mail: { file: "outlook-mail.svg", label: "Microsoft Outlook" },
  overture: { file: "overture.svg", label: "Overture" },
  overture_sun: { file: "overture-sun.svg", label: "Overture and SUN" },
  postgres: { file: "postgres.svg", label: "Postgres" },
  salesforce: { file: "salesforce.svg", label: "Salesforce" },
  sharepoint: { file: "sharepoint.svg", label: "SharePoint" },
  slack: { file: "slack.png", label: "Slack" },
  smartlead: { file: "smartlead.svg", label: "Smartlead" },
  stripe: { file: "stripe.svg", label: "Stripe" },
  sun: { file: "sun.svg", label: "SUN Systems" },
  tallie: { file: "tallie.svg", label: "Tallie" },
  teams: { file: "teams.svg", label: "Microsoft Teams" },
  twin: { file: "workspace.svg", label: "Twin", root: MLX_ICON_ROOT },
  xero: { file: "xero.svg", label: "Xero" },
};

const SOURCE_ALIASES: Record<string, keyof typeof SOURCE_ICONS> = {
  "google-calendar": "google_calendar",
  "google-docs": "google_docs",
  "google-drive": "google_drive",
  "google-gmail": "gmail",
  "google-sheets": "google_sheets",
  "google-slides": "google_slides",
  "microsoft-teams": "teams",
  "office-365": "microsoft_365",
  "mlx-artifacts": "mlx_artifacts",
  "mlx-warehouse": "mlx_warehouse",
  "mlx-workspace": "mlx_workspace",
  outlook: "outlook_mail",
  "outlook-mail": "outlook_mail",
  "overture-sun": "overture_sun",
  sunsystems: "sun",
};

const CLIENT_ICONS: Record<string, Omit<BrandIcon, "src"> & { file: string }> =
  {
    chatgpt: { file: "chatgpt.svg", label: "ChatGPT" },
    claude: { file: "claude.svg", label: "Claude" },
    codex: { file: "codex.svg", label: "Codex", monochrome: true },
    gemini: { file: "gemini.svg", label: "Gemini" },
  };

const CLIENT_ALIASES: Record<string, keyof typeof CLIENT_ICONS> = {
  "claude-code": "claude",
};

function normalise(id: string): string {
  return id.trim().toLowerCase();
}

/** Connector or product mark for an id, or null when none is registered. */
export function sourceIcon(id: string): BrandIcon | null {
  const key = normalise(id);
  const entry = SOURCE_ICONS[SOURCE_ALIASES[key] ?? key];
  if (!entry) return null;
  return {
    src: `${entry.root ?? SOURCE_ICON_ROOT}/${entry.file}`,
    label: entry.label,
    ...(entry.monochrome ? { monochrome: true } : {}),
  };
}

/** AI-client mark for a client app id, or null when none is registered. */
export function clientIcon(id: string): BrandIcon | null {
  const key = normalise(id);
  const entry = CLIENT_ICONS[CLIENT_ALIASES[key] ?? key];
  if (!entry) return null;
  return {
    src: `${CLIENT_ICON_ROOT}/${entry.file}`,
    label: entry.label,
    ...(entry.monochrome ? { monochrome: true } : {}),
  };
}

/**
 * The third-party marks a canvas may show, by kebab-case name (connector and product marks, then the
 * AI clients), with the names that fold onto them. MLX's own marks are left out: a canvas draws the
 * Twin with the kit's TwinMark.
 */
export function brandMarks(): {
  marks: { name: string; label: string; file: string; monochrome: boolean }[];
  aliases: Record<string, string>;
} {
  const kebab = (id: string) => id.replace(/_/g, "-");
  const marks = [
    ...Object.entries(SOURCE_ICONS)
      .filter(([, entry]) => !entry.root)
      .map(([id, entry]) => ({ name: kebab(id), label: entry.label, file: `icons/sources/${entry.file}`, monochrome: Boolean(entry.monochrome) })),
    ...Object.entries(CLIENT_ICONS)
      .map(([id, entry]) => ({ name: kebab(id), label: entry.label, file: `icons/clients/${entry.file}`, monochrome: Boolean(entry.monochrome) })),
  ];
  const known = new Set(marks.map((mark) => mark.name));
  const aliases = Object.fromEntries([...Object.entries(SOURCE_ALIASES), ...Object.entries(CLIENT_ALIASES)]
    .map(([alias, id]) => [alias, kebab(id)] as const)
    .filter(([alias, name]) => alias !== name && known.has(name)));
  return { marks, aliases };
}

/**
 * Retired marks served only because admin plugin catalogue versions before
 * 089 name them as the Google Docs, Drive and Sheets plugins' iconUrl. Delete
 * these files and this list once every target is bound to catalogue 089 or
 * later.
 */
export const CATALOGUE_RETAINED_ICON_FILES = [
  "google-docs.svg",
  "google-drive.svg",
  "google-sheets.svg",
] as const;

/** Asset file names the registry expects under each public directory. */
export const REGISTERED_ICON_FILES: Record<string, readonly string[]> = {
  "icons/sources": [
    ...new Set([
      ...Object.values(SOURCE_ICONS)
        .filter((entry) => !entry.root)
        .map((entry) => entry.file),
      ...CATALOGUE_RETAINED_ICON_FILES,
    ]),
  ],
  "icons/mlx": [
    ...new Set(
      Object.values(SOURCE_ICONS)
        .filter((entry) => entry.root === MLX_ICON_ROOT)
        .map((entry) => entry.file),
    ),
  ],
  "icons/clients": [
    ...new Set(Object.values(CLIENT_ICONS).map((entry) => entry.file)),
  ],
};
